import Input from '@orn/shared-web/components/Input/Input';
import Select, { type SelectItemType } from '@orn/shared-web/components/Select/Select';
import SelectItem from '@orn/shared-web/components/Select/SelectItem';

import { HousingOwnership, ResidenceType } from '../../../__generated__/globalTypes';
import { HOME_OWNERSHIP_OPTIONS, RESIDENCE_TYPE_OPTIONS } from '../../../lib/dependents/constants';

import type { IResidenceFieldErrors } from './dependentCardModel';
import type { IResidenceFormState } from './residenceForm';

interface IResidenceTypeFieldsProps {
  state: IResidenceFormState;
  /** Patch the residence-type slice of the form state. */
  onChange: (patch: Partial<IResidenceFormState>) => void;
  /** Persist pending edits — fired when a Select changes or a describe field blurs. */
  onCommit?: () => void;
  /** Live errors for the linked living-situation unit (the address error renders with the address field, not here). */
  errors?: IResidenceFieldErrors;
  /** Label on the residence-type Select. Defaults to "Living situation". */
  label?: string;
  isDisabled?: boolean;
}

/**
 * The dependents settings form's residence-type cascade: a "living situation"
 * Select, a free-text describe field when the type is `Other`, and — for
 * independent living — a home-ownership Select with its own `Other` describe
 * field. Drives the `residenceType` / `residenceTypeOther` / `homeOwnership` /
 * `homeOwnershipOther` slices of `IResidenceFormState`. Purely controlled — no
 * internal state.
 */
const ResidenceTypeFields = ({
  state,
  onChange,
  onCommit,
  errors,
  label = 'Living situation',
  isDisabled,
}: IResidenceTypeFieldsProps) => (
  <>
    <Select
      label={label}
      placeholder="Select"
      selectedKey={state.residenceType}
      onSelectionChange={(key) => {
        onChange({ residenceType: key as ResidenceType });
        onCommit?.();
      }}
      items={RESIDENCE_TYPE_OPTIONS}
      isDisabled={isDisabled}
      isInvalid={Boolean(errors?.residenceType)}
      hint={errors?.residenceType}
    >
      {(item: SelectItemType) => <SelectItem id={item.id} label={item.label} />}
    </Select>

    {state.residenceType === ResidenceType.Other && (
      <Input
        label="Describe their living situation"
        placeholder="e.g. Temporary rehabilitation facility"
        value={state.residenceTypeOther}
        onChange={(v) => onChange({ residenceTypeOther: v })}
        onBlur={onCommit}
        isDisabled={isDisabled}
        isInvalid={Boolean(errors?.residenceTypeOther)}
        hint={errors?.residenceTypeOther}
      />
    )}

    {state.residenceType === ResidenceType.IndependentLiving && (
      <>
        <Select
          label="Home ownership"
          placeholder="Select"
          selectedKey={state.homeOwnership ?? undefined}
          onSelectionChange={(key) => {
            onChange({ homeOwnership: key as HousingOwnership });
            onCommit?.();
          }}
          items={HOME_OWNERSHIP_OPTIONS}
          isDisabled={isDisabled}
          isInvalid={Boolean(errors?.homeOwnership)}
          hint={errors?.homeOwnership}
        >
          {(item: SelectItemType) => <SelectItem id={item.id} label={item.label} />}
        </Select>

        {state.homeOwnership === HousingOwnership.Other && (
          <Input
            label="Describe the ownership situation"
            placeholder="e.g. Co-owned with sibling"
            value={state.homeOwnershipOther}
            onChange={(v) => onChange({ homeOwnershipOther: v })}
            onBlur={onCommit}
            isDisabled={isDisabled}
            isInvalid={Boolean(errors?.homeOwnershipOther)}
            hint={errors?.homeOwnershipOther}
          />
        )}
      </>
    )}
  </>
);
ResidenceTypeFields.displayName = 'ResidenceTypeFields';

export default ResidenceTypeFields;
